import { Image, Tag } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import entete from "../../../../assets/img/entete_pool.png";
import { chunkArray, displayDateEcheance, frenchDate, frenchDateSimple } from "../../../../variables/functions";
import { API_URL, TOKEN } from "../../../../variables/constants";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DocumentFooter } from "../../../../components/docs/DocumentFooter";
import BarreTop from "../../../../components/docs/BarreTop";
import { getCompagnieLogo } from "../../../../hooks/useFilesHooks";

const styles = {
  fontFamily: "Arial, sans-serif",
  fontSize: "11px",
  color: "black",
  stamp: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(-45deg)",
    color: "red",
    fontSize: "72px",
    fontWeight: "bold",
    opacity: 0.5,
    zIndex: 1000,
    pointerEvents: "none",
    border: "5px solid red",
    padding: "20px",
    textAlign: "center",
  },
};
const thinBorder = "0.6px solid #666";

const resolveFractionnementLabels = (fractionnement) => {
  const defaultDisplay = "Annuel";
  const defaultBase = "ANNUEL";

  if (!fractionnement) {
    return { display: defaultDisplay, base: defaultBase };
  }

  const directLabel = fractionnement?.libelleFractionnement || fractionnement?.nom;
  if (directLabel && directLabel.trim()) {
    const trimmed = directLabel.trim();
    return { display: trimmed, base: trimmed.toUpperCase() };
  }

  const periode = (fractionnement?.periode || "").toUpperCase();
  const moisMin = Number(fractionnement?.moisMin);

  if (periode === "MOIS") {
    const mapping = {
      12: "Annuel",
      6: "Semestriel",
      3: "Trimestriel",
      2: "Bimestriel",
      1: "Mensuel",
    };

    if (Number.isFinite(moisMin) && mapping[moisMin]) {
      const label = mapping[moisMin];
      return { display: label, base: label.toUpperCase() };
    }

    if (Number.isFinite(moisMin) && moisMin > 0) {
      const plural = moisMin > 1 ? "mois" : "mois";
      const label = `${moisMin} ${plural}`;
      return { display: label, base: label.toUpperCase() };
    }
  }

  if (periode === "JOUR") {
    if (Number.isFinite(moisMin) && moisMin > 0) {
      const plural = moisMin > 1 ? "jours" : "jour";
      const label = `${moisMin} ${plural}`;
      return { display: label, base: label.toUpperCase() };
    }
  }

  return { display: defaultDisplay, base: defaultBase };
};

const computeFractionnementDetails = (contrat, primes) => {
  const fractionnement = contrat?.fractionnement;
  const { display, base } = resolveFractionnementLabels(fractionnement);

  const parsedNombre = Number(fractionnement?.nombre);
  const count = Number.isFinite(parsedNombre) && parsedNombre > 0 ? parsedNombre : 1;
  const hasFractionnement = primes?.fractionnement;

  const parsedRate = Number(primes?.fractionnement);
  const rate = Number.isFinite(parsedRate) ? parsedRate : null;

  const primeNetteAnnuelle = Number(contrat?.primeNette) || 0;
  const primeTotaleAnnuelle = Number(contrat?.primeTotale) || 0;
  const accessoires = Number(contrat?.accessoires) || 0;

  let primeNetteFractionnee = null;
  if (hasFractionnement) {
    const provided = Number(contrat?.primeNetteFractionnee);
    if (Number.isFinite(provided)) {
      primeNetteFractionnee = provided;
    } else {
      primeNetteFractionnee = (primeNetteAnnuelle + accessoires) / count;
    }
  }

  const primeTotaleFractionnee = hasFractionnement ? primeTotaleAnnuelle / count : null;

  const echeanceLabel = count > 1 ? `${count} échéances` : "1 échéance";
  const modalite = hasFractionnement
    ? `${echeanceLabel}${rate !== null ? ` - Majoration ${rate}%` : ""}`
    : "Paiement unique";

  const summary = hasFractionnement
    ? `${display || "Fractionné"} (${echeanceLabel}${rate !== null ? ` - Majoration ${rate}%` : ""})`
    : `${display || "Annuel"} (paiement unique)`;

  return {
    base,
    display: display || "Annuel",
    summary,
    modalite,
    hasFractionnement,
    count,
    rate,
    primeNetteAnnuelle,
    primeTotaleAnnuelle,
    primeNetteFractionnee,
    primeTotaleFractionnee,
  };
};

const DTRFFormat = React.forwardRef((props, ref) => {
  const [date, setDate] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [primes, setPrimes] = useState("");
  const avenantCode = props?.contrat?.avenant?.code;
  const avenantEffectiveDate =
    props?.contrat?.avenant?.dateEffet ||
    props?.contrat?.avenant?.dateEffetAvenant ||
    props?.contrat?.dateEffetAvenant ||
    null;
  const shouldDisplayAvenantDate =
    ["AG", "DG"].includes(avenantCode || "") && avenantEffectiveDate;

  useEffect(() => {
    if (!props?.contrat?.codeId) {
      return;
    }

    axiosInstance.get(`/api/contrat/preview/${props?.contrat?.codeId}`).then((res) => {
      console.log("PREVIEW: ", res.data);
      setPrimes(res.data);
    }).catch((err) => {
      console.log(err);
      toast("Une erreur est survenue");
    });
    let dte = new Date();
    // console.log("DS:", dte.toDateString());
    // console.log("DS:", dte.toISOString());
    setDate(dte.toISOString());

    if (props?.contrat?.organisme?.logo) {
      getCompagnieLogo(props?.contrat?.organisme?.code)
        .then(async (response) => {
          // console.log("The response", response);

          // convert byte to file
          const imageBytes = await response.arrayBuffer();
          // console.log("images byte: ", imageBytes);
          var blob = new Blob([imageBytes], { type: "image/png" });
          // console.log("Blob: ", blob);
          var imageUrl = URL.createObjectURL(blob);
          // console.log("Image URL: ", imageUrl);
          setLogoPreviewUrl(imageUrl);
          // console.log("File: ", file);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    if (props?.contrat?.intermediaire) {
      if (props?.contrat?.intermediaire?.agence?.signature) {
        fetch(
          `${API_URL}/api/agence/signature/${props?.contrat?.intermediaire?.agence?.signature?.split("/")[1]
          } `,
          {
            headers: {
              Authorization: `Bearer ${TOKEN} `,
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
              "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
              "Access-Control-Allow-Headers":
                "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
            },
          }
        )
          .then(async (response) => {
            // console.log("The response", response);
            // convert byte to file
            const imageBytes = await response.arrayBuffer();
            // console.log("images byte: ", imageBytes);
            var blob = new Blob([imageBytes], { type: "image/png" });
            // console.log("Blob: ", blob);
            var imageUrl = URL.createObjectURL(blob);
            console.log("Image URL: ", imageUrl);
            setImagePreviewUrl(imageUrl);
            // console.log("File: ", file);

            setTimeout(() => {
              console.log("Preview: ", { imagePreviewUrl });
            }, 1000);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else if (
      props?.contrat?.producteur != null &&
      props?.contrat?.producteur?.agence?.signature
    ) {
      fetch(
        `${API_URL}/api/agence/signature/${props?.contrat?.producteur?.agence?.signature?.split("/")[1]
        } `,
        {
          headers: {
            Authorization: `Bearer ${TOKEN} `,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
          },
        }
      )
        .then(async (response) => {
          // console.log("The response", response);
          // convert byte to file
          const imageBytes = await response.arrayBuffer();
          // console.log("images byte: ", imageBytes);
          var blob = new Blob([imageBytes], { type: "image/png" });
          // console.log("Blob: ", blob);
          var imageUrl = URL.createObjectURL(blob);
          console.log("Image URL: ", imageUrl);
          setImagePreviewUrl(imageUrl);
          // console.log("File: ", file);

          setTimeout(() => {
            console.log("Preview: ", { imagePreviewUrl });
          }, 1000);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [
    props?.contrat?.codeId,
    props?.contrat?.organisme?.logo,
    props?.contrat?.intermediaire,
    props?.contrat?.producteur
  ]);

  const fractionnementDetails = computeFractionnementDetails(props?.contrat, primes);
  console.log("Fractionnement details: ", fractionnementDetails);
  const {
    base: fractionnementBareme,
    display: fractionnementTypeLabel,
    summary: fractionnementSummary,
    modalite: fractionnementModalite,
    hasFractionnement,
    primeNetteAnnuelle,
    primeTotaleAnnuelle,
    primeNetteFractionnee,
  } = fractionnementDetails;

  const bareme = fractionnementBareme || "ANNUEL";

  const duree = props.contrat?.fractionnement
    ? `${props.contrat?.fractionnement.moisMin} Mois`
    : props?.contrat?.engins && props?.contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("VT")
      ? "15 jours"
      : "12 Mois";

  const primeNetteFractionneeDisplay = (hasFractionnement !== 100) && primeNetteFractionnee !== null
    ? displayNumbers(primeNetteFractionnee)
    : (hasFractionnement !== 100)
      ? "-"
      : displayNumbers(primeNetteAnnuelle);

  const fractionnementDisplay = fractionnementSummary || ((hasFractionnement !== 100) ? fractionnementTypeLabel : "Paiement unique");
  const echeanceDisplay = (hasFractionnement !== 100) ? fractionnementModalite : "Paiement unique";

  const reduData = [
    {
      key: "1",
      libelle: "Fractionnement",
      montant: fractionnementSummary,
      align: "left",
    },
    {
      key: "2",
      libelle: "Réduction flotte",
      montant: props?.contrat?.engins?.length > 1 ? "15%" : "0%",
    },
    {
      key: "3",
      libelle: "BNS",
      montant: `${displayNumbers(primes?.bonusMalus ?? 0)}%`,
    },
    {
      key: "4",
      libelle: (hasFractionnement !== 100)
        ? "Prime nette fractionnée (par échéance)"
        : "Prime nette annuelle",
      montant: primeNetteFractionneeDisplay,
    },
  ];

  const primeData = [
    {
      key: "1",
      libelle: "Prime Nette",
      montant: `${displayNumbers(props?.contrat?.primeNette)} `,
    },
    {
      key: "2",
      libelle: "Coût de pièces",
      montant: `${displayNumbers(props?.contrat?.accessoires)} `,
    },
    {
      key: "3",
      libelle: "Taxes",
      montant: `6 % (${displayNumbers((props?.contrat?.primeTotale * 0.06) / 1.06)})`,
    },
    {
      key: "4",
      libelle: "Prime Totale",
      montant: `${displayNumbers(props?.contrat?.primeTotale)} `,
    },
  ];

  useEffect(() => {
    if (props?.contrat?.engins.length > 1) {
      import("./styles/flotteCPStyle.css");
    }
  }, [props?.contrat?.engins.length]);



  const rowsPerPage = 18;
  const tableChunks = chunkArray(primes?.engins || [], rowsPerPage);
  const titreSouscripteur =
    props.contrat?.souscripteur?.typePersonne === "PHYSIQUE"
      ? props.contrat?.souscripteur?.genre === "MASCULIN"
        ? "M."
        : props.contrat?.souscripteur?.genre === "FEMININ"
          ? "Mme"
          : ""
      : "Société";

  const bonusMalus = primes?.bonusMalus;

  let bonusMalusText = "";

  if (typeof bonusMalus === "number" && bonusMalus !== 0) {
    const sign = bonusMalus > 0 ? "-" : "+";

    bonusMalusText = `(BNS sur RC) ${sign} ${displayNumbers(
      Math.abs(bonusMalus)
    )}%`;
  }

  return props?.contrat?.engins && props?.contrat?.engins.length === 1 ? (
    <div className="col-12" style={styles} ref={ref}>
      <div
        className="card"
        style={{ position: "relative", minHeight: "100vh" }}
      >
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 0px",
            }}
          >
            {props?.contrat?.organisme?.typeOrganisme !== "POOL" && (
              <Image
                src={logoPreviewUrl}
                style={{
                  width: "100%",
                  height: "105px",
                }}
              />
            )}

            <Image
              src={entete}
              style={{
                width: "100%",
                height: "105px",
              }}
            />
          </div>
          <BarreTop />
        </div>

        {/* --- Nouveau bloc pour le titre de l'avenant --- */}
        {props?.contrat?.avenant?.code && props?.contrat?.avenant?.code !== "AN" && (
          <div className="text-center" style={{ marginTop: '-5px' }}>
            <h5 style={{ fontWeight: 'bold', color: '#272366', marginBottom: '0px', marginTop: '0px' }}>
              {props?.contrat?.avenant?.code === 'AG' && "AVENANT D'AUGMENTATION DE GARANTIE"}
              {props?.contrat?.avenant?.code === 'DG' && "AVENANT DE DIMINUTION DE GARANTIE"}
              {props?.contrat?.avenant?.code === 'RV' && "AVENANT DE REMISE EN VIGUEUR"}
              {props?.contrat?.avenant?.code === 'RN' && "AVENANT DE RENOUVELLEMENT"}
              {props?.contrat?.avenant?.code === 'SP' && "AVENANT DE SUSPENSION"}
              {props?.contrat?.avenant?.code === 'RS' && "AVENANT DE RESILIATION"}
              {props?.contrat?.avenant?.code === 'RE' && "AVENANT DE REPORT D'EFFET"}
              {props?.contrat?.avenant?.code === 'CV' && "AVENANT DE CHANGEMENT DE VEHICULE"}
              {props?.contrat?.avenant?.code === 'PR' && "AVENANT DE PROROGATION"}
              {props?.contrat?.avenant?.code === 'TG' && "AVENANT DE TRANSFERT DE GARANTIES"}
              {props?.contrat?.avenant?.code === 'RT' && "AVENANT DE RETRAIT"}
              {/* Ajoutez d'autres conditions pour les codes d'avenant si nécessaire */}
            </h5>
            {shouldDisplayAvenantDate && (
              <p style={{ marginBottom: '2px', marginTop: '4px', color: '#272366', fontSize: '12px' }}>
                Date d'effet de l'avenant : {frenchDate(avenantEffectiveDate)}
              </p>
            )}
            <hr className="my-2" style={{ color: "black", marginTop: '0px', marginBottom: '0px' }} />
          </div>
        )}
        {/* --- Fin du nouveau bloc --- */}

        <div
          style={{
            padding: "1rem",
            paddingTop: "5px",
            paddingBottom: "100px",
            flex: 1,
            overflow: "auto",
          }}
        >
          <div className="">
            <div className="row">
              <div className="col-12">
                {/* {props?.contrat?.avenant?.code === "SP" ? (
                  <div style={styles.stamp}>CONTRAT SUSPENDU</div>
                ) : null} */}
                <table className="" style={{ width: "100%" }}>
                  <tbody>
                    <tr>
                      <td className="text-left" style={{ color: "black" }}>
                        Site :
                      </td>
                      <td className="text-right" style={{ color: "#272366" }}>
                        {props.contrat?.producteur?.agence
                          ? `${props.contrat?.producteur?.agence?.nom} `
                          : `Siège ${props.contrat?.organisme?.raisonSociale} `}
                      </td>
                      <td className="text-right"></td>
                      <td className="text-right"></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td></td>
                      <td></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td className="text-left" style={{ color: "black" }}>
                        Police/Avenant N° :
                      </td>
                      <td
                        className="text-right"
                        style={{ color: "#272366" }}
                        colSpan={2}
                      >
                        {props.contrat?.numeroAvenant ? props.contrat?.numeroAvenant : props.contrat?.numeroPoliceP ? props.contrat?.numeroPoliceP : props.contrat?.numeroPoliceM}
                      </td>
                      <td className="text-right"></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td className="text-left" style={{ color: "black" }}>
                        Effet :
                      </td>
                      <td className="text-right" style={{ color: "#272366" }}>
                        {frenchDate(props.contrat?.dateEffet)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-left" style={{ color: "black" }}>
                        N° Assuré :
                      </td>
                      <td className="text-right" style={{ color: "#272366" }}>
                        {props.contrat?.souscripteur.numeroAssureP ? props.contrat?.souscripteur?.numeroAssureP : props.contrat?.souscripteur?.numeroAssureM}
                      </td>
                      <td className="text-right"></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td className="text-left" style={{ color: "black" }}>
                        Apporteur :
                      </td>
                      <td
                        className="text-right"
                        style={{ color: "#272366" }}
                        colSpan={2}
                      >
                        {props.contrat?.intermediaire
                          ? `${props.contrat?.intermediaire?.personne?.nom} ${props.contrat?.intermediaire?.personne?.prenom} `
                          : props.contrat?.courtier?.nom}
                      </td>
                      <td className="text-right"></td>
                      <td>
                        <div className=""></div>
                      </td>
                      <td className="text-left" style={{ color: "black" }}>
                        Echéance :
                      </td>
                      <td className="text-right" style={{ color: "#272366" }}>
                        {displayDateEcheance(props.contrat?.dateEcheance)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <hr className="my-2" style={{ color: "black" }} />
            <div className="row">
              <div className="col-12 text-left" style={{ fontSize: "13px" }}>
                {(props?.contrat?.avenant?.code === 'AN' || props?.contrat?.avenant?.code === 'RN' || props?.contrat?.avenant?.code === 'PR') && `Aux Conditions Générales de la Police qui précèdent et à celles particulières qui suivent, les Compagnies d'Assurances IARDT assurent le véhicule ci-dessous désigné suivant les garanties accordées. Ainsi, sont
                          nulles toutes adjonctions ou modifications matérielles
                          non revêtues du visa de la direction ou du
            représentant.`}
                {props?.contrat?.avenant?.code === 'SP' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci - dessus référencée sont suspendues pour une durée maximum d'un (01) an à compter de la date de suspension.
                                                            Si l’assuré ne remet pas l’assurance en vigueur avant l’expiration d'un (01) an, la Police susvisée sera résiliée de plein droit. `}
                {
                  props?.contrat?.avenant?.code === 'RE' && `D’un commun accord entre les parties, l’effet du contrat ci-dessus est reporté à la date effet sus-mentionnée.
                                                            Ledit contrat cours désormais jusqu’ à la date échéance ci-dessus. `}
                {
                  props?.contrat?.avenant?.code === 'RV' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci-dessus référencée sont remises en vigueur à compter de la date effet sus-mentionnée.
                                                            L’échéance est fixée à la date d'échéance sus-mentionnée. `}
                {
                  props?.contrat?.avenant?.code === 'IN' && `D’un commun accord entre les parties il est convenu que les véhicules ci-joints sont incorporés au présent contrat aux conditions mentionnées ci-dessus. 
                                                            L’assuré s’engage à payer à l’assureur la prime totale ci-dessus décomptée.`}
                {props?.contrat?.avenant?.code === 'RS' && `A la demande de l’assuré et d’un commun accord entre les parties, la Police sus-mentionnée faisant l’objet du présent avenant est résiliée à compter de la date effet su-mentionnée et cesse dès lors de produire ses effets à l’égard des parties. En conséquence la compagnie s’engage à ristourner à l’assuré la portion de prime correspondant à la période non courue.`}
                {props?.contrat?.avenant?.code === 'RT' && `Le souscripteur déclare et la compagnie lui en donne acte que les engins ci-joints sont retirés et modifient la Police ci-dessus référencée à compter de la date effet sus-mentionnée. En conséquence, il est ristourné au souscripteur à la signature du présent avenant, pour la période de la date effet sus-mentionnée jusqu’ à la date d’echeance ci-dessus le montant total ci-dessus décompté.`}
              </div >
            </div >
            <hr className="my-2" style={{ color: "black" }} />
            <div className="text-center">
              <h6 style={{ marginBottom: "0px" }} >Conditions Particulières - ASSURANCE AUTOMOBILE</h6>
              <h7 style={{ fontSize: "13px", fontWeight: "bold", color: "#272366" }}>
                <u>CONTRAT A DUREE FERME</u>
              </h7>
            </div>
          </div >
          <div className="row">
            <div className="col-12">
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr>
                    <td className="text-start">
                      <strong>SOUSCRIPTEUR : </strong>{" "}
                      {props.contrat?.souscripteur?.prenom
                        ? `${props.contrat?.souscripteur.nom} ${props.contrat?.souscripteur?.prenom}`
                        : `${props.contrat?.souscripteur.nom}`}{" "}
                      (ASSURE:{" "}
                      {props.contrat?.assure?.prenom
                        ? `${props?.contrat?.assure?.nom} ${props?.contrat?.assure?.prenom}`
                        : `${props?.contrat?.assure?.nom}`}
                      )
                    </td>
                    <td className=""></td>
                    <td>{/* <div className="w-100"></div> */}</td>
                    <td className=""></td>
                    <td className="text-end">
                      <strong>PRODUCTEUR : </strong>
                      {props?.contrat?.producteur?.personne?.prenom}{" "}
                      {props?.contrat?.producteur?.personne?.nom}
                    </td>
                  </tr>
                </tbody>
              </table>
              <hr className="my-2" style={{ color: "black" }} />
              <table
                // className="table table-borderless"
                style={{ width: "100%" }}
              >
                <tbody>
                  <tr>
                    <td className="text-start">
                      <strong>Profession : </strong>
                      {props.contrat?.souscripteur.profession}
                    </td>
                    <td className=""></td>
                    <td>{/* <div className="w-100"></div> */}</td>
                    <td className=""></td>
                    <td className="text-end">
                      <strong>Adresse : </strong>
                      {props.contrat?.souscripteur.adresse} {props.contrat?.souscripteur.contact ? `(${props.contrat?.souscripteur.contact[0]})` : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
              <hr className="my-2" style={{ color: "black" }} />
              <div className="text-center" style={styles}>
                <h6 style={{ marginBottom: "0px" }}>
                  IDENTIFICATION VEHICULE - N° Immatriculation :{" "}
                  <strong>{primes?.engins && primes?.engins[0].immatriculation}</strong>
                </h6>
                <hr className="my-2" style={{ color: "black" }} />
              </div>
              <div class="">
                <div class="row">
                  <div class="col-12">
                    <table
                      style={{
                        width: "100%",
                        fontSize: "12px",
                        lineHeight: "1.4",
                      }}
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              width: "12%",
                              fontWeight: "bold",
                              color: "black",
                            }}
                          >
                            Marque :
                          </td>
                          <td style={{ width: "20%", color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].marque}
                          </td>
                          <td
                            style={{
                              width: "12%",
                              fontWeight: "bold",
                              color: "black",
                            }}
                          >
                            Carrosserie :
                          </td>
                          <td style={{ width: "20%", color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].carosserie}
                          </td>
                          <td
                            style={{
                              width: "12%",
                              fontWeight: "bold",
                              color: "black",
                            }}
                          >
                            Année :
                          </td>
                          <td style={{ width: "10%", color: "#272366" }}>
                            {
                              primes?.engins && primes?.engins[0].dateMiseEnCirculation?.split(
                                "-"
                              )[0]
                            }
                          </td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Puissance :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].categorie
                              .codeCategorie !== "M"
                              ? `${primes?.engins && (primes?.engins[0].volume || primes?.engins[0].puissance)} CV`
                              : primes?.engins && primes?.engins[0].volume ? `${primes?.engins && primes?.engins[0].volume} cm³` : `${primes?.engins && primes?.engins[0].puissance}`}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Énergie :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].energie}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            N° Châssis :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].numeroChassis}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Valeur vénale :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {displayNumbers(
                              primes?.engins && primes?.engins[0].valeurVenale
                            )}{" "}
                            FCFA
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Places :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].nombrePlace}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Genre :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].genre}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            N° Moteur :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {primes?.engins && primes?.engins[0].moteur}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Valeur à neuf :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {displayNumbers(
                              primes?.engins && primes?.engins[0].valeurANeuf
                            )}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Catégorie :
                          </td>
                          <td style={{ color: "#272366" }}>
                            {props.contrat?.engins && (props.contrat?.engins[0]?.categorie?.abbreviation || props.contrat?.engins[0]?.categorie?.nom)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Usage :
                          </td>
                          <td style={{ color: "#272366" }} colSpan="2">
                            {props?.contrat?.engins && props?.contrat?.engins[0].usage}
                          </td>
                          <td style={{ fontWeight: "bold", color: "black" }}>
                            Lieu garage :
                          </td>
                          <td colSpan="2">...</td>
                        </tr>
                      </tbody>
                    </table>

                    <hr className="my-2" style={{ color: "black" }} />
                    <div className="">
                      {/* ===== TABLEAU DES GARANTIES ===== */}
                      <table
                        className="table table-bordered"
                        style={{
                          border: "1px solid black",
                          fontSize: "11px",
                        }}
                      >
                        <thead>
                          <tr>
                            {[
                              "Garanties",
                              "Capitaux",
                              ...((hasFractionnement !== 100)
                                ? ["Prime (FCFA)"]
                                : ["Prime (En FCFA)"])
                            ].map((label, i) => (
                              <th
                                key={i}
                                style={{
                                  border: "1px solid black",
                                  textAlign: "center",
                                  padding: "4px",
                                  backgroundColor: "#e6e9f8",
                                  color: "#272366",
                                }}
                              >
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {(() => {
                            const garantiesTransformees = primes?.compulsoryGuaranties?.reduce((acc, g) => {
                              if (g.nom != null && (g.nom?.toUpperCase()?.startsWith("RC") || g.nom.toUpperCase()?.startsWith("SURPRIME"))) {
                                const rcIndex = acc.findIndex(item => item.nom === "RC");
                                if (rcIndex === -1) {
                                  acc.push({ ...g, nom: "RC" });
                                } else {
                                  acc[rcIndex].primeNetteAnnuelle += g.primeNetteAnnuelle;
                                  acc[rcIndex].primeNette += g.primeNette;
                                }
                              } else {
                                if (g.nom == null) {
                                  g.nom = "RC";
                                }
                                acc.push(g);
                              }
                              return acc;
                            }, []);

                            return garantiesTransformees?.map((dt, id) => (
                              <tr key={id} style={{ border: "1px solid black", lineHeight: dt.nom === "Individuel Conducteur" ? "1.1" : "1" }}>

                                {/* Garantie */}
                                <td style={{ border: "1px solid black", padding: "3px", verticalAlign: "top" }}>
                                  {dt.nom === "Individuel Conducteur" &&
                                    props?.contrat?.engins[0]?.categorie?.codeCategorie === "M" ? (
                                    <table style={{ fontSize: "10px", width: "100%" }}>
                                      <tbody>
                                        <tr><td colSpan="2">Individuel Conducteur</td></tr>
                                        <tr><td>* Décès :</td><td></td></tr>
                                        <tr><td>* Invalidité :</td><td></td></tr>
                                        <tr><td>* Frais Médicaux :</td><td></td></tr>
                                      </tbody>
                                    </table>
                                  ) : (
                                    dt.nom
                                  )}
                                </td>

                                {/* Capitaux */}
                                <td style={{ border: "1px solid black", padding: "3px", textAlign: "right", verticalAlign: "top" }}>
                                  {dt.nom === "Individuel Conducteur" &&
                                    props?.contrat?.engins[0]?.categorie?.codeCategorie === "M" ? (
                                    <table style={{ fontSize: "10px", width: "100%" }}>
                                      <tbody>
                                        <tr><td></td><td>-</td></tr>
                                        <tr><td></td><td>250 000</td></tr>
                                        <tr><td></td><td>500 000</td></tr>
                                        <tr><td></td><td>50 000</td></tr>
                                      </tbody>
                                    </table>
                                  ) : (
                                    displayNumbers(dt.capital)
                                  )}
                                </td>

                                {/* Prime Fractionnée — uniquement si fractionnement */}
                                {(hasFractionnement !== 100) ? (
                                  <td style={{
                                    border: "1px solid #272366",
                                    padding: "3px",
                                    textAlign: "right",
                                    verticalAlign: "middle",
                                    color: "#272366",
                                    fontWeight: "bold",
                                  }}>
                                    {displayNumbers((dt.primeNette))}
                                  </td>
                                ) :
                                  <td style={{ border: "1px solid black", padding: "3px", textAlign: "right", verticalAlign: "middle" }}>
                                    {displayNumbers(dt.primeNetteAnnuelle)}
                                  </td>
                                }
                              </tr>
                            ));
                          })()}
                          {/* Ligne de total — remplace le tfoot pour garantir l'ordre visuel */}
                          {(hasFractionnement !== 100) && (
                            <tr>
                              <td
                                colSpan={2}
                                style={{
                                  border: "1px solid black",
                                  padding: "3px",
                                  textAlign: "right",
                                  fontWeight: "bold",
                                  backgroundColor: "#e6e9f8",
                                  color: "#272366",
                                }}
                              >
                                Total primes nettes {bonusMalusText}
                              </td>
                              <td style={{
                                border: "1px solid black",
                                padding: "3px",
                                textAlign: "right",
                                fontWeight: "bold",
                                backgroundColor: "#e6e9f8",
                                color: "#272366",
                              }}>
                                {(hasFractionnement !== 100) ? displayNumbers(primes?.primeNetteFractionnee) : displayNumbers(primes?.primeNette)}
                              </td>
                            </tr>
                          )}
                        </tbody>

                        {/* Second tbody — récapitulatif des primes et taxes */}
                        <tbody>
                          {/* Durée + Prime Nette */}
                          <tr style={{ lineHeight: "0.1" }}>
                            <td style={{ border: "none", whiteSpace: "nowrap" }} colSpan={2}>
                              <strong>
                                Durée :{" "}
                                {props?.contrat?.fractionnement
                                  ? `${props.contrat?.fractionnement.moisMin} Mois`
                                  : props?.contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                                    ? "15 JOURS"
                                    : "12 MOIS"}
                              </strong>
                            </td>
                            <td style={{ border: "1px solid #ccc", padding: "3px", textAlign: "right", whiteSpace: "nowrap" }}>
                              <span style={{ color: "#555" }}>Coût de pièces :</span>{" "}
                              {(hasFractionnement !== 100) ? displayNumbers(primes?.accessoiresFractionnee) : displayNumbers(primes?.accessoires)}
                            </td>
                          </tr>

                          {/* Réduction flotte + Prime Totale */}
                          <tr style={{ lineHeight: "0.1" }}>
                            <td style={{ border: "none", whiteSpace: "nowrap" }}>Réduction flotte :</td>
                            <td style={{ border: "none", textAlign: "right", paddingRight: "8px" }}>
                              {primes?.flotte ? `${primes?.flotte}%` : "0%"}
                            </td>
                            <td style={{ border: "1px solid #ccc", padding: "3px", textAlign: "right", whiteSpace: "nowrap" }}>
                              <span style={{ color: "#555" }}>Taxes (6%) :</span>{" "}
                              {(hasFractionnement !== 100) ? displayNumbers(primes?.taxesFractionnee) : displayNumbers((primes?.taxes))}
                            </td>
                          </tr>

                          {/* BNS + Coût pièces */}
                          <tr style={{ lineHeight: "0.1" }}>
                            <td style={{ border: "none", whiteSpace: "nowrap" }}>BNS :</td>
                            <td style={{ border: "none", textAlign: "right", paddingRight: "8px" }}>
                              {`${primes?.bonusMalus || 0}%`}
                            </td>
                            <td style={{
                              border: "1px solid #272366",
                              padding: "4px 5px",
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              backgroundColor: "#e6e9f8",
                            }}>
                              <b style={{ color: "#272366", fontWeight: "bold", fontSize: "13px", fontFamily: "Arial, sans-serif" }}>
                                Prime TTC à payer : {props?.contrat?.primeTotale && displayNumbers((primes?.primeTotale || primes?.primeTotaleAnnuelle))}
                              </b>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <br />
                      <div class="row">
                        <div class="col-12">
                          <table class="table w-90">
                            <tr>
                              <td class="text-right">
                                <strong style={{ color: "black" }}>
                                  LE SOUSCRIPTEUR
                                </strong>
                              </td>
                              <td class="w-30"></td>
                              <td>
                                <div class="w-40"></div>
                              </td>
                              <td class="text-right">
                                <strong style={{ color: "black" }}>
                                  DATE :
                                </strong>
                              </td>
                              <td class="w-40"></td>
                              <td>
                                <div class="w-100"></div>
                              </td>
                              <td class="text-right">
                                <strong style={{ color: "black" }}>
                                  POUR LA COMPAGNIE :
                                </strong>
                              </td>
                              <td colspan="2" class="text-right"></td>
                            </tr>
                            <tr>
                              <td class="text-right">
                                <strong style={{ color: "black" }}></strong>
                              </td>
                              <td class="w-30"></td>
                              <td>
                                <div class="w-40"></div>
                              </td>
                              <td class="text-right">
                                <strong style={{ color: "black" }}>
                                  {frenchDate(date)}
                                </strong>
                              </td>
                              <td class="w-40"></td>
                              <td>
                                <div class="w-100"></div>
                              </td>
                              <td class="text-right">
                                {imagePreviewUrl ? (
                                  <img
                                    class="img-fluid rounded mb-4"
                                    src={imagePreviewUrl}
                                    height="220"
                                    width="220"
                                    alt="User avatar"
                                  />
                                ) : null}
                              </td>
                              <td colspan="2" class="text-right"></td>
                            </tr>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Continue with more Bootstrap code, replacing Grid and Material UI components */}
        </div >
        <div style={{ flexShrink: 0 }}>
          {props?.printing ? <p style={{ marginLeft: "15px" }} >Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}</p> : props?.contrat?.dateImpression ? <p style={{ marginLeft: "15px" }} >Imprimé le : {frenchDate(props?.contrat?.dateImpression)}</p> : null}
          <DocumentFooter />
        </div>
      </div >
    </div >
  ) : (
    <div class="col-12" ref={ref}>
      <div className="card" style={{ position: "relative", minHeight: "100vh" }}>
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 0px",
            }}
          >
            {props?.contrat?.organisme?.typeOrganisme !== "POOL" && <Image
              src={logoPreviewUrl}
              style={{
                width: "100%",
                height: "105px",
              }}
            />}

            <Image
              src={entete}
              style={{
                width: "100%",
                height: "105px",
              }}
            />
          </div>

          <BarreTop />
        </div>
        <div
          style={{
            padding: "1rem",
            paddingTop: "10px",
            paddingBottom: "105px",
            flex: 1,
            overflow: "auto",
          }}
        >
          <div class="text-center">
            <h5 className="my-0">
              {/* Conditions Particulières{" "}
              {props.contrat?.engins[0]?.categorie?.nom} (Flotte) */}

              {/* --- Nouveau bloc pour le titre de l'avenant --- */}
              {props?.contrat?.avenant?.code && props?.contrat?.avenant?.code !== "AN" ? (
                <div className="text-center" style={{ marginTop: '10px', marginBottom: '10px' }}>

                  <hr className="my-0" style={{ color: "black" }} />
                  <h5 style={{ fontWeight: 'bold', color: '#272366' }}>
                    {props?.contrat?.avenant?.code === 'AG' && "AVENANT D'AUGMENTATION DE GARANTIE"}
                    {props?.contrat?.avenant?.code === 'DG' && "AVENANT DE DIMINUTION DE GARANTIE"}
                    {props?.contrat?.avenant?.code === 'RV' && "AVENANT DE REMISE EN VIGUEUR"}
                    {props?.contrat?.avenant?.code === 'RN' && "AVENANT DE RENOUVELLEMENT"}
                    {props?.contrat?.avenant?.code === 'SP' && "AVENANT DE SUSPENSION"}
                    {props?.contrat?.avenant?.code === 'RS' && "AVENANT DE RESILIATION"}
                    {props?.contrat?.avenant?.code === 'RE' && "AVENANT DE REPORT D'EFFET"}
                    {props?.contrat?.avenant?.code === 'CV' && "AVENANT DE CHANGEMENT DE VEHICULE"}
                    {props?.contrat?.avenant?.code === 'IN' && "AVENANT D'INCROPORATION"}
                    {props?.contrat?.avenant?.code === 'PR' && "AVENANT DE PROROGATION"}
                    {props?.contrat?.avenant?.code === 'TG' && "AVENANT DE TRANSFERT DE GARANTIES"}
                    {props?.contrat?.avenant?.code === 'RT' && "AVENANT DE RETRAIT"}
                    {/* Ajoutez d'autres conditions pour les codes d'avenant si nécessaire */}
                  </h5>
                  {shouldDisplayAvenantDate && (
                    <p style={{ marginBottom: '4px', color: '#272366', fontSize: '12px' }}>
                      Date d'effet de l'avenant : {frenchDate(avenantEffectiveDate)}
                    </p>
                  )}
                  <hr className="my-0" style={{ color: "black" }} />
                </div>
              ) : `Conditions Particulières ${props.contrat?.engins[0]?.categorie?.nom} (Flotte)`}
              {/* --- Fin du nouveau bloc --- */}
            </h5>
          </div>
          <div className="row">
            <div className="col-12">
              {/* En-tête */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <h5 style={{ margin: 0, marginLeft: "25px", fontWeight: "bold" }}>Référence Souscripteur</h5>
                <h5 style={{ margin: 0, marginRight: "145px", fontWeight: "bold" }}>Référence de la Police</h5>
              </div>
              <hr style={{ color: "black", margin: "8px 0" }} />

              {/* Tableau sans bordures */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", lineHeight: "2.2" }}>
                <tbody>
                  <tr>
                    <td colSpan="2"> <span style={{ width: "12%", fontWeight: "bold", color: "black" }}>Code Assuré :</span>  {props.contrat?.souscripteur?.numeroAssureM ? props.contrat?.souscripteur?.numeroAssureM : props?.contrat?.souscripteur?.numeroAssureP}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>N° Quittance :</strong> -</td>
                    <td><strong style={{ fontWeight: "bold", color: "black", fontSize: "10px" }}>N° Avenant :</strong> {props?.contrat?.numeroAvenant}</td>
                  </tr>
                  <tr>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Titre : </strong>{titreSouscripteur} </td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>N° Police :</strong> {props.contrat?.numeroPoliceP}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>Avenant :</strong> {props.contrat?.avenant?.nom}</td>
                  </tr>
                  <tr>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Nom :</strong> {props.contrat?.souscripteur?.nom} {props.contrat?.souscripteur?.prenom || ""}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>Effet :</strong> {props.contrat?.dateEffet && frenchDate(props.contrat?.dateEffet)}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>Echéance :</strong> {props.contrat?.dateEcheance && frenchDateSimple(props.contrat?.dateEcheance)}</td>
                  </tr>
                  <tr>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Adresse :</strong> {props.contrat?.souscripteur?.adresse}</td>
                    <td colSpan="2">
                      <strong style={{ fontWeight: "bold", color: "black" }}>Catégorie :</strong>
                      {(props?.contrat?.engins[0]?.categorie?.abbreviation || props?.contrat?.engins[0]?.categorie?.nom)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Tel :</strong> {props.contrat?.souscripteur?.contact?.join(" / ")}</td>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Mouvement :</strong> {props.contrat?.avenant?.nom}</td>
                  </tr>
                  <tr>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Email :</strong> {props.contrat?.souscripteur?.email || "-"}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>Ecriture :</strong> {bareme}</td>
                    <td><strong style={{ fontWeight: "bold", color: "black" }}>Durée :</strong> {duree}</td>
                  </tr>
                  <tr>
                    <td colSpan="2">
                      <strong style={{ fontWeight: "bold", color: "black" }}>Site de production :</strong>
                      {props.contrat?.producteur?.agence
                        ? props.contrat?.producteur?.agence?.nom
                        : `Siège ${props.contrat?.organisme?.raisonSociale}`}
                    </td>
                    <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Type emission :</strong> SANS TACITE RECONDUCTION</td>
                  </tr>
                  <tr>
                    <td colSpan="2">
                      <strong style={{ fontWeight: "bold", color: "black" }}>Intermédiaire :</strong>
                      {props.contrat?.intermediaire
                        ? `${props.contrat?.intermediaire?.personne?.nom} ${props.contrat?.intermediaire?.personne?.prenom}`
                        : props.contrat?.courtier?.nom || "-"}
                    </td>
                    <td colSpan="2">
                      <strong style={{ fontWeight: "bold", color: "black" }}>Producteur :</strong> {props?.contrat?.producteur?.personne?.nom} {props?.contrat?.producteur?.personne?.prenom}
                    </td>
                  </tr>
                </tbody>
              </table>
              <hr className="my-2" style={{ color: "black" }} />
              <div className="row">
                <div className="col-12 text-left" style={{ fontSize: "15px" }}>
                  {(props?.contrat?.avenant?.code === 'AN' || props?.contrat?.avenant?.code === 'RN') && `Aux Conditions Générales de la Police qui précèdent et à celles particulières qui suivent, les Compagnies d'Assurances IARDT assurent le véhicule ci-dessous désigné suivant les garanties accordées. Ainsi, sont
                          nulles toutes adjonctions ou modifications matérielles
                          non revêtues du visa de la direction ou du
                          représentant.`}
                  {props?.contrat?.avenant?.code === 'SP' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci-dessus référencée sont suspendues pour une durée maximum d'un (01) an à compter de la date de suspension.
                                                            Si l’assuré ne remet pas l’assurance en vigueur avant l’expiration d'un (01) an, la Police susvisée sera résiliée de plein droit. `}
                  {props?.contrat?.avenant?.code === 'RE' && `D’un commun accord entre les parties, l’effet du contrat ci-dessus est reporté à la date effet sus-mentionnée.
                                                            Ledit contrat cours désormais jusqu’ à la date échéance ci-dessus. `}
                  {props?.contrat?.avenant?.code === 'RV' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci-dessus référencée sont remises en vigueur à compter du [%RUB_Effet%].
                                                            L’échéance est fixée au [%RUB_Expiration%]. `}
                  {props?.contrat?.avenant?.code === 'IN' && `D’un commun accord entre les parties il est convenu que les véhicules ci-joints sont incorporés au présent contrat aux conditions mentionnées ci-dessus. 
                                                            L’assuré s’engage à payer à l’assureur la prime totale ci-dessus décomptée.`}
                  {props?.contrat?.avenant?.code === 'RS' && `A la demande de l’assuré et d’un commun accord entre les parties, la Police sus-mentionnée faisant l’objet du présent avenant est résiliée à compter de la date effet su-mentionnée et cesse dès lors de produire ses effets à l’égard des parties. En conséquence la compagnie s’engage à ristourner à l’assuré la portion de prime correspondant à la période non courue.`}
                  {props?.contrat?.avenant?.code === 'RT' && `Le souscripteur déclare et la compagnie lui en donne acte que les engins ci-joints sont retirés et modifient la Police ci-dessus référencée à compter de la date effet sus-mentionnée. En conséquence, il est ristourné au souscripteur à la signature du présent avenant, pour la période de la date effet sus-mentionnée jusqu’ à la date d’echeance ci-dessus le montant total ci-dessus décompté.`}
                </div >
              </div>
              <hr className="my-2" style={{ color: "black" }} />
            </div>
          </div>
          <br />
          <div className="row">
            <div className="col-12">
              <div className="card p-2">
                {/* Compact Table */}
                <table
                  className="table table-sm table-bordered mb-3"
                  style={{
                    border: "1px solid black",
                    fontSize: "12px",
                    borderCollapse: "collapse",
                  }}
                >
                  <tbody>
                    {reduData?.map((rd, id) => (
                      <tr key={id}>
                        {/* Reduction Label */}
                        <td
                          style={{
                            border: "1px solid black",
                            textAlign: "center",
                            padding: "2px 4px",
                            backgroundColor: "#e6e9f8",
                            color: "#272366",
                            fontWeight: "700",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rd.libelle}
                        </td>

                        {/* Reduction Montant */}
                        <td
                          style={{
                            border: "1px solid black",
                            textAlign: rd.align || "right",
                            verticalAlign: "middle",
                            padding: "2px 6px",
                            fontWeight: "700",
                          }}
                        >
                          {rd.montant}
                        </td>

                        {/* Prime Label */}
                        <td
                          style={{
                            border: "1px solid black",
                            textAlign: "center",
                            padding: "2px 4px",
                            backgroundColor: "#e6e9f8",
                            color: "#272366",
                            fontWeight: "700",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {primeData[id].libelle}
                        </td>

                        {/* Prime Montant */}
                        <td
                          style={{
                            border: "1px solid black",
                            textAlign: "right",
                            verticalAlign: "middle",
                            padding: "2px 6px",
                            fontWeight: "700",
                          }}
                        >
                          {primeData[id].montant}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <br />
                <br />
                <br />
                {/* Signature / Date Section */}

              </div>
              <div className="row">
                <table
                  className="table table-borderless table-sm w-100"
                  style={{ fontSize: "12px" }}
                >
                  <tbody>
                    <tr>
                      <td className="text-right fw-bold text-dark">LE SOUSCRIPTEUR</td>
                      <td></td>
                      <td></td>
                      <td className="text-right fw-bold text-dark">DATE :</td>
                      <td></td>
                      <td></td>
                      <td className="text-right fw-bold text-dark">
                        POUR LA COMPAGNIE :
                      </td>
                      <td className="text-right"></td>
                    </tr>
                    <tr>
                      <td className="text-right fw-bold text-dark"></td>
                      <td></td>
                      <td></td>
                      <td>{frenchDate(date)}</td>
                      <td className="text-right fw-bold text-dark"></td>
                      <td></td>
                      <td className="text-right">
                        {imagePreviewUrl && (
                          <img
                            src={imagePreviewUrl}
                            height="80"
                            alt="Signature"
                            style={{ objectFit: "contain" }}
                          />
                        )}
                      </td>
                      <td className="text-right fw-bold text-dark"> </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Footer Image */}
              {/* <Image src={footer} alt="Footer Image" className="mt-2" /> */}
            </div>
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          {props?.printing ? <p style={{ marginLeft: "15px" }} >Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}</p> : props?.contrat?.dateImpression ? <p style={{ marginLeft: "15px" }} >Imprimé le : {frenchDate(props?.contrat?.dateImpression)}</p> : null}
          <DocumentFooter />
        </div>
      </div>
      {tableChunks.map((chunk, pageIndex) => (
        <div
          key={pageIndex}
          className="print-page"
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "297mm",
            maxHeight: "297mm",
            padding: "10mm",
            boxSizing: "border-box",
            pageBreakAfter: chunk.length === rowsPerPage ? "always" : "auto", // break after full pages
          }}
        >
          {/* Page Header (repeated on each page) */}
          <div className="print-header" style={{ flexShrink: 0, marginBottom: "5mm" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0px 0px",
              }}
            >
              {props?.contrat?.organisme?.typeOrganisme !== "POOL" && <Image
                src={logoPreviewUrl}
                style={{
                  width: "100%",
                  height: "105px",
                }}
              />}

              <Image
                src={entete}
                style={{
                  width: "100%",
                  height: "105px",
                }}
              />
            </div>

            <BarreTop />
          </div>

          {/* Table (only the chunk for this page) */}
          <div className="table-container" style={{ flex: 1, overflow: "hidden" }}>
            <table
              className="table table-bordered"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Immatriculation</th>
                  <th>Risques</th>
                  <th>Prime nette</th>
                </tr>
              </thead>
              <tbody>
                {chunk.map((item, index) => {
                  const isNew =
                    item.createdAt &&
                    (props?.contrat?.updatedAt || props?.contrat?.dateEffet) &&
                    new Date(item.createdAt).toDateString() ===
                    new Date(props?.contrat?.updatedAt || props?.contrat?.dateEffet).toDateString();

                  // Transform risques string into grouped data
                  const grouped = {};

                  // console.log("Risques: ", item);
                  let hasNewRisque = false;
                  item?.compulsoryGuaranties?.forEach((risque) => {
                    const { nom, primeNette } = risque;
                    const amount = parseFloat(primeNette) || 0;

                    let groupKey = nom;
                    if (nom?.toUpperCase()?.startsWith("RC") || nom?.toUpperCase()?.startsWith("SURPRIME")) {
                      groupKey = "RC";
                    }

                    if (!grouped[groupKey]) {
                      grouped[groupKey] = { label: groupKey, total: 0 };
                    }
                    grouped[groupKey].total += amount;
                    grouped[groupKey].new = risque.recentlyAdded;
                    if (risque.recentlyAdded) {
                      hasNewRisque = true;
                    }
                  });

                  const risquesGrouped = Object.values(grouped);
                  return (
                    <tr
                      key={index}
                      style={isNew ? { backgroundColor: "#e6fffb" } : {}}
                    >
                      <td>
                        {index + 1 + rowsPerPage * pageIndex} {isNew && <span style={{ color: "red" }}>+</span>}
                      </td>
                      <td>{item.immatriculation}</td>
                      <td>
                        {risquesGrouped.map((r, ind) => (
                          <div
                            key={ind}
                            style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}
                          >
                            <Tag color={r.new ? "gray" : "blue"}>{r.new ? <span style={{ color: "blue" }}>+</span> : ""} {r.label}</Tag>
                            <Tag color="green">
                              {displayNumbers(r.total)} FCFA
                            </Tag>
                          </div>
                        ))}
                      </td>
                      <td>{hasNewRisque && `(${displayNumbers(risquesGrouped?.reduce((total, r) => total + (!r.new ? 0 : r.total), 0) || 0)})`} {displayNumbers(item.primeNette)} FCFA</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Page Footer (repeated on each page) */}
          <div className="print-footer" style={{ flexShrink: 0, marginTop: "5mm" }}>
            <DocumentFooter />
          </div>
        </div>
      ))}
    </div>
  );
});

DTRFFormat.propTypes = {
  contrat: PropTypes.object,
  printing: PropTypes.bool,
};
export default DTRFFormat;
